import React, { useState, useEffect } from 'react';
import api from '../api';
import toast from 'react-hot-toast';
import { exportCSV } from '../utils/exportCSV';

function fmtDuration(secs) {
  if (!secs) return '0s';
  const m = Math.floor(secs / 60), s = secs % 60;
  return m > 0 ? `${m}m ${s}s` : `${s}s`;
}

const MEDAL = ['#1', '#2', '#3'];

export default function Reports() {
  const [funnel, setFunnel] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [performance, setPerformance] = useState([]);
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [callDetail, setCallDetail] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const [funnelRes, lbRes, perfRes, usersRes] = await Promise.all([
          api.get('/reports/funnel'),
          api.get('/reports/leaderboard'),
          api.get('/reports/performance'),
          api.get('/users')
        ]);
        setFunnel(funnelRes.data);
        setLeaderboard(lbRes.data);
        setPerformance(perfRes.data);
        setUsers(usersRes.data);
      } catch (e) {
        toast.error('Failed to load reports');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const fetchCallDetail = async () => {
    if (!selectedUser) return toast.error('Select an employee');
    try {
      const params = new URLSearchParams({ userId: selectedUser });
      if (from) params.append('from', from);
      if (to) params.append('to', to);
      const { data } = await api.get(`/reports/calls?${params}`);
      setCallDetail(data);
    } catch (e) {
      toast.error('Failed to load call detail');
    }
  };

  const exportLeaderboard = () => {
    const rows = leaderboard.map((e, i) => ({
      Rank: i + 1,
      Employee: e.name,
      Role: e.role,
      'Calls This Week': e.calls,
      Interested: e.interested,
      'Demo Scheduled': e.demoScheduled,
      'Avg Duration': fmtDuration(e.avgDuration)
    }));
    exportCSV('leaderboard.csv', rows, ['Rank', 'Employee', 'Role', 'Calls This Week', 'Interested', 'Demo Scheduled', 'Avg Duration']);
  };

  const totalCalls = performance.reduce((s, e) => s + e.totalCalls, 0);
  const totalInterested = performance.reduce((s, e) => s + e.interested, 0);
  const totalDemo = performance.reduce((s, e) => s + e.scheduledDemo, 0);
  const interestedPct = totalCalls > 0 ? Math.round((totalInterested / totalCalls) * 100) : 0;

  if (loading) return (
    <div className="fadein">
      <div className="page-header"><div className="page-title">Reports</div></div>
      <div className="empty"><div className="empty-icon"></div><p>Loading reports...</p></div>
    </div>
  );

  return (
    <div className="fadein">
      <div className="page-header">
        <div>
          <div className="page-title">Reports & Analytics</div>
          <div className="page-subtitle">Performance overview</div>
        </div>
      </div>

      {/* Summary stat cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 24 }}>
        {[
          { label: 'Total Leads',     value: funnel?.totalLeads ?? 0, color: 'var(--accent)' },
          { label: 'Total Calls',     value: totalCalls,              color: 'var(--violet)' },
          { label: 'Interested %',    value: `${interestedPct}%`,     color: 'var(--green)' },
          { label: 'Demo Scheduled',  value: totalDemo,               color: 'var(--amber)' },
        ].map(s => (
          <div key={s.label} className="stat-card">
            <div style={{ fontSize: 11, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>{s.label}</div>
            <div style={{ fontSize: 26, fontWeight: 700, color: s.color, lineHeight: 1 }}>{s.value}</div>
          </div>
        ))}
      </div>

      {/* Leaderboard */}
      <div className="card" style={{ marginBottom: 20 }}>
        <div className="card-header">
          <span className="card-header-title">Leaderboard — This Week</span>
          <button className="btn btn-secondary btn-sm" onClick={exportLeaderboard}>Export CSV</button>
        </div>
        {leaderboard.length === 0 ? (
          <div className="empty"><div className="empty-icon"></div><p>No data this week</p></div>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Rank</th>
                <th>Employee</th>
                <th>Calls</th>
                <th>Interested</th>
                <th>Demo Scheduled</th>
                <th>Avg Duration</th>
              </tr>
            </thead>
            <tbody>
              {leaderboard.map((e, i) => (
                <tr key={e._id}>
                  <td>
                    <span style={{ fontWeight: 700, fontSize: 14 }}>
                      {i < 3 ? <span style={{ fontWeight: 700, color: 'var(--accent)' }}>{MEDAL[i]}</span> : <span style={{ color: 'var(--text-3)' }}>#{i + 1}</span>}
                    </span>
                  </td>
                  <td style={{ fontWeight: 500 }}>{e.name}</td>
                  <td style={{ fontWeight: 600, color: 'var(--accent)' }}>{e.calls}</td>
                  <td style={{ color: 'var(--green)', fontWeight: 500 }}>{e.interested}</td>
                  <td style={{ color: 'var(--violet)', fontWeight: 500 }}>{e.demoScheduled}</td>
                  <td style={{ color: 'var(--text-3)', fontSize: 13 }}>{fmtDuration(e.avgDuration)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Funnel */}
      {funnel && (
        <div className="card" style={{ marginBottom: 20, padding: 20 }}>
          <div style={{ fontWeight: 600, fontSize: 13.5, color: 'var(--text-1)', marginBottom: 16 }}>Lead Funnel</div>
          {[
            { label: 'Total Leads',    count: funnel.totalLeads,    color: 'var(--accent)',  base: funnel.totalLeads },
            { label: 'Called',         count: funnel.called,        color: 'var(--violet)',  base: funnel.totalLeads },
            { label: 'Interested',     count: funnel.interested,    color: 'var(--green)',   base: funnel.totalLeads },
            { label: 'Demo Scheduled', count: funnel.demoScheduled, color: 'var(--amber)',   base: funnel.totalLeads },
          ].map(row => {
            const pct = row.base > 0 ? Math.round((row.count / row.base) * 100) : 0;
            return (
              <div key={row.label} style={{ marginBottom: 14 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 5, fontSize: 13 }}>
                  <span style={{ fontWeight: 500, color: 'var(--text-1)' }}>{row.label}</span>
                  <span style={{ color: 'var(--text-3)' }}>{row.count} ({pct}%)</span>
                </div>
                <div className="progress-bar" style={{ height: 8 }}>
                  <div className="progress-fill" style={{ width: `${pct}%`, background: row.color }} />
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Per-employee detail */}
      <div className="card" style={{ padding: 20 }}>
        <div style={{ fontWeight: 600, fontSize: 13.5, color: 'var(--text-1)', marginBottom: 16 }}>Employee Call Detail</div>
        <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
          <select value={selectedUser} onChange={e => setSelectedUser(e.target.value)} style={{ minWidth: 200 }}>
            <option value="">Select employee</option>
            {users.map(u => <option key={u._id} value={u._id}>{u.name}</option>)}
          </select>
          <input type="date" value={from} onChange={e => setFrom(e.target.value)} style={{ width: 160 }} />
          <input type="date" value={to} onChange={e => setTo(e.target.value)} style={{ width: 160 }} />
          <button className="btn btn-primary btn-sm" onClick={fetchCallDetail}>Load</button>
        </div>

        {callDetail && (
          <div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10, marginBottom: 16 }}>
              {[
                { label: 'Total Calls',  value: callDetail.total },
                { label: 'Avg Duration', value: fmtDuration(callDetail.avgDuration) },
                { label: 'Statuses',     value: Object.keys(callDetail.byStatus).length },
              ].map(s => (
                <div key={s.label} className="stat-card" style={{ padding: '12px 14px' }}>
                  <div style={{ fontSize: 11, color: 'var(--text-3)', textTransform: 'uppercase', marginBottom: 4 }}>{s.label}</div>
                  <div style={{ fontSize: 20, fontWeight: 700, color: 'var(--text-1)' }}>{s.value}</div>
                </div>
              ))}
            </div>
            <table>
              <thead>
                <tr><th>Status</th><th>Count</th></tr>
              </thead>
              <tbody>
                {Object.entries(callDetail.byStatus).map(([status, count]) => (
                  <tr key={status}>
                    <td><span className={`badge badge-${status}`}>{status.replace(/_/g, ' ')}</span></td>
                    <td style={{ fontWeight: 600 }}>{count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
